import { Navigate, useLocation } from "react-router-dom";

/**
 * Phase 4 — SEO-safe legacy alias redirect helper.
 *
 * Client-side <Navigate replace> is our only redirect mechanism on Lovable's
 * default hosting (SPA fallback serves index.html for unknown paths; there is
 * no _redirects/_headers processing — see spa-routing knowledge). This
 * component makes those redirects behave predictably by:
 *
 *  - Preserving the original `?search` string
 *  - Preserving the original `#hash`
 *  - Using `replace` so the alias URL does not accumulate in browser history
 *
 * NOTE (hosting limitation): this is NOT an HTTP 301/308. Search engines that
 * execute JS will follow the redirect and consolidate signals over time, but
 * crawlers that only read raw HTML see a client redirect. That's acceptable
 * for legacy aliases that are ALSO removed from the sitemap + SEO manifest
 * (indexable=false, inSitemap=false) so we do not actively invite indexing.
 */
export default function AliasRedirect({ to }: { to: string }) {
  const { search, hash } = useLocation();
  // If the target already contains a hash, prefer the caller's hash.
  const [targetPath, targetHash] = to.split("#");
  const finalHash = targetHash ? `#${targetHash}` : hash || "";
  return <Navigate to={`${targetPath}${search}${finalHash}`} replace />;
}
